import React from "react";
import {
  SortableContainer,
  SortableElement,
  arrayMove,
} from "react-sortable-hoc";
import KMBDropzone from "@layout/KMBDropzone";
import PropTypes from "prop-types";
import XLSHelper from "@helpers/xls-helper";
import KMBLoader from "@layout/KMBLoader";
import Select from "@layout/Select";
import Radio from "@layout/Radio";
import { downloadFile } from "@helpers";
import SubmitContainer from "@layout/SubmitContainer";

const SortableItem = SortableElement(({ value }) => {
  return <li>{value.label}</li>;
});

const SortableList = SortableContainer(({ items, mergedSettings }) => {
  return (
    <ol className="fields-wrapper">
      {items.map((k, index) => {
        return (
          <SortableItem
            key={`item-${index}`}
            index={index}
            value={mergedSettings[k]}
          />
        );
      })}
    </ol>
  );
});

export default class BulkImportSessions extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      activeFields: ["name", "startDate", "endDate", "eventRoomId"],
      file: false,
      preview: [],
      buildingPreview: false,
      extension: null,
      seperator: "comma",
      ignoreFirstRow: false,
    };

    this.selectField = this.selectField.bind(this);
    this.processCsv = this.processCsv.bind(this);
    this.onSortEnd = this.onSortEnd.bind(this);
    this.exportTemplate = this.exportTemplate.bind(this);

    this.mergedSettings = {
      eventRoomId: { label: "Room ID", required: true },
      name: { label: "Name", required: true },
      startDate: { label: "Start Date", required: true },
      endDate: { label: "End Date", required: true },
      description: { label: "Description" },
      urlAlias: { label: "Url Alias" },
      questions: { label: "User can ask questions" },
      questionsEmail: { label: "Questions email" },
      urlStream: { label: "Url stream" },
      videoUrl: { label: "Video url" },
    };
  }

  componentDidMount() {
    this.props.loadRooms(this.props.eventId);
  }

  ignoreFirstRow(value) {
    const ignoreFirstRow = value === "yes";
    this.setState({ ignoreFirstRow }, () => {
      this.previewFile(this.state.file[0], this.state.seperator).then(
        ({ preview }) => this.setState({ preview })
      );
    });
  }

  exportTemplate(e, excel = false) {
    e.preventDefault();
    const rows = [];
    const header = [];
    const demoRow = [];
    const { urlAlias } = this.props.event;
    const fileName = `${urlAlias}-session-template`;

    for (let i = 0; i < this.state.activeFields.length; i++) {
      const field = this.state.activeFields[i];
      header.push(
        this.mergedSettings[field].required === true ? `${field} *` : field
      );
      if (field === "eventRoomId") {
        demoRow.push(this.props.rooms.length ? this.props.rooms[0].id : "");
      } else if (["startDate", "endDate"].includes(field)) {
        demoRow.push("DD-MM-YYYY HH:mm");
      } else {
        demoRow.push("Dummy Data");
      }
    }

    rows.push(header);
    rows.push(demoRow);

    if (excel) {
      // excel template
      return XLSHelper.exportDefaultXLS(rows, fileName);
    }
    // csv template
    let csvContent = "data:text/csv;charset=utf-8,";
    rows.forEach((rowArray) => {
      const row = rowArray.join(",");
      csvContent += row + "\r\n";
    });

    downloadFile(
      encodeURI(csvContent),
      fileName + ".csv",
      document.getElementsByClassName("exportables")[0]
    );
  }

  changeSeperator(seperator) {
    this.setState(
      {
        seperator,
      },
      () => {
        this.previewFile(this.state.file[0], seperator).then(({ preview }) =>
          this.setState({ preview, buildingPreview: false })
        );
      }
    );
  }

  onSortEnd({ oldIndex, newIndex }) {
    this.setState({
      activeFields: arrayMove(this.state.activeFields, oldIndex, newIndex),
    });
  }

  selectField(e) {
    const id = e.target.id;
    const activeFields = [...this.state.activeFields];
    const index = activeFields.indexOf(id);
    if (index !== -1) {
      activeFields.splice(index, 1);
    } else {
      activeFields.push(id);
    }
    this.setState({ activeFields });
  }

  previewFile(file, seperator = "comma") {
    return new Promise((resolve, reject) => {
      const extension = file.name.split(".").pop();
      const reader = new FileReader();
      // Read file into memory as UTF-8

      extension === "csv"
        ? reader.readAsText(file)
        : reader.readAsBinaryString(file);

      const { ignoreFirstRow } = this.state;

      // Handle errors load
      reader.onload = (e) => {
        const tfile = e.target.result;
        const preview =
          extension === "csv"
            ? this.processCsv(tfile, seperator, ignoreFirstRow)
            : XLSHelper.read(tfile, ignoreFirstRow);
        preview !== false && resolve({ preview, extension });
      };
      reader.onerror = (e) => {
        reject(e.target.error.name);
      };
    });
  }

  processCsv(csv, seperator = "comma", ignoreFirstRow = false) {
    seperator = seperator === "comma" ? "," : ";";

    const rows = ignoreFirstRow ? 2 : 1;

    const allTextLines = csv.split(/\r\n|\n/);
    const preview = [];
    if (allTextLines.length === 0) {
      return preview;
    }
    for (let i = 0; i <= rows; i++) {
      if (ignoreFirstRow && i === 0) continue;
      const data = allTextLines[i].split(seperator);
      const tarr = [];
      for (let j = 0; j < data.length; j++) {
        tarr.push(data[j]);
      }
      preview.push(tarr);
    }

    return preview;
  }

  render() {
    const { extension, seperator } = this.state;
    return (
      <div className="form-container bulk-import">
        <h2>Import Sessions</h2>
        <div className="guide">
          <h3>Fields Selection</h3>
          <p>
            Please select from the above list the fields that are present to the
            CSV or XLS file that you would like to select by checking. You can
            rearrange the order in which the fileds are present in the file. If
            the file uploaded has fields that you do not wish to include in the
            created users data, just uncheck the field. Once you are happy with
            the order click the import button.
          </p>
        </div>
        <div className="row">
          <div className="col-sm-6">
            <div className="available-fields">
              <h3>Available Session Fields</h3>
              <ul className="fields-wrapper">
                {Object.entries(this.mergedSettings).map(([k, v]) => {
                  return (
                    <li className="kmb-checkbox" key={k}>
                      <input
                        type="checkbox"
                        id={k}
                        onChange={this.selectField}
                        checked={this.state.activeFields.includes(k)}
                        disabled={v.hasOwnProperty("required") && v.required}
                      />
                      <label htmlFor={k}>{v.label}</label>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
          <div className="col-sm-6">
            <div className="available-rooms">
              <h3>Rooms Map ( Name - ID )</h3>
              <div className="med-table-container ">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Room Name</th>
                      <th>Room ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {this.props.rooms.map((r, i) => (
                      <tr key={`r-${i}`}>
                        <td>{r.name}</td>
                        <td>{r.id}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="selected-fields">
          <h3>Selected Fields in CSV or XLS file</h3>
          {this.state.activeFields.length > 0 && (
            <SortableList
              items={this.state.activeFields}
              mergedSettings={this.mergedSettings}
              onSortEnd={this.onSortEnd}
              lockAxis="y"
            />
          )}
          {this.state.activeFields.length === 0 && (
            <p>No fields selected yet.</p>
          )}
          {this.state.activeFields.length > 0 && (
            <span className="exportables">
              <a
                href="#"
                className="export-template"
                onClick={(e) => this.exportTemplate(e)}
              >
                Download CSV Template
              </a>
              <a
                href="#"
                className="export-template"
                onClick={(e) => this.exportTemplate(e, true)}
              >
                Download XLSX Template
              </a>
            </span>
          )}
        </div>
        <div className="drop-area">
          <h3>CSV or XLS file</h3>
          <KMBDropzone
            active={this.state.activeFields.length > 0}
            promptMessage={[
              <span key="decorative" className="icon-cloud"></span>,
            ]}
            accept=".csv,.xls,.xlsx"
            styleType="simple"
            onFileRemove={() => this.setState({ file: false, preview: [] })}
            onDropRejected={(message) => {
              this.props.addNotification(message, "error");
              this.setState({
                file: false,
                preview: [],
                extension: null,
                seperator: "comma",
              });
            }}
            onDropAccepted={(file) => {
              this.setState(
                {
                  file,
                  buildingPreview: true,
                },
                () => {
                  this.previewFile(this.state.file[0])
                    .then(({ preview, extension }) =>
                      this.setState({
                        preview,
                        buildingPreview: false,
                        extension,
                      })
                    )
                    .catch((err) => {
                      this.setState(
                        { buildingPreview: false },
                        this.props.addNotification("error", err)
                      );
                    });
                }
              );
            }}
            style={{
              width: "100%",
              height: "auto",
              borderWidth: 1,
              borderColor: "#1DA4CF",
              borderStyle: "dashed",
              borderRadius: 4,
              backgroundImage: "none",
              padding: "25px 5px",
              backgroundColor: "#fff",
            }}
            activeStyle={{
              borderStyle: "dashed",
              borderColor: "#1DA4CF",
            }}
            acceptStyle={{
              borderStyle: "dashed",
              borderColor: "#14DA9E",
            }}
            rejectStyle={{
              borderStyle: "dashed",
              borderColor: "blue",
            }}
          ></KMBDropzone>
          {this.state.buildingPreview && (
            <KMBLoader rows={15} padding={24} height={53} />
          )}
          {this.state.preview.length > 0 && (
            <div className="preview-panel">
              {extension === "csv" && (
                <div className="csv-seperator">
                  <h3>CSV Seperator</h3>
                  <Select
                    options={{
                      comma: "Comma",
                      semicolon: "Semicolon",
                    }}
                    value={seperator}
                    placeholderInsideSelect={false}
                    onChange={(sep) => this.changeSeperator(sep)}
                  />
                </div>
              )}
              <div className="ignore-first">
                <h3>Ignore First Row ( Header )</h3>
                <div className="form-container">
                  <div className="field-type type-radio kmb-radio">
                    <Radio
                      checked={this.state.ignoreFirstRow ? "yes" : "no"}
                      onChange={(val) => this.ignoreFirstRow(val)}
                    />
                  </div>
                </div>
              </div>
              <h3>Preview File ( Two first rows )</h3>
              <div
                className="med-table-container "
                style={{ overflow: "auto" }}
              >
                <table className="table">
                  <thead>
                    <tr>
                      {this.state.activeFields.map((f, i) => (
                        <th key={`a-${i}`}>{f}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {this.state.preview.map((d, index) => (
                      <tr key={`row-${index}`}>
                        {this.state.activeFields.map((f, i) => (
                          <td key={`pre-${index}-${i}`}>{d[i]}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p>If you are happy with the result, please click import.</p>
            </div>
          )}
        </div>
        <SubmitContainer
          onCancel={this.props.hideModal}
          onSubmit={() =>
            this.props.importSessions(
              this.props.eventId,
              this.state.activeFields,
              this.state.file[0],
              this.state.ignoreFirstRow,
              this.state.seperator === "comma" ? "," : ";"
            )
          }
          submitText="Import"
        />
      </div>
    );
  }
}

BulkImportSessions.propTypes = {
  eventId: PropTypes.string.isRequired,
  rooms: PropTypes.array.isRequired,
  sessionTypes: PropTypes.array.isRequired,
  addNotification: PropTypes.func.isRequired,
  importSessions: PropTypes.func.isRequired,
  hideModal: PropTypes.func.isRequired,
  loadRooms: PropTypes.func.isRequired,
  event: PropTypes.object.isRequired,
};
